import apiClient from './fetch'
import mime from 'mime-types'

const client = apiClient()

export const paths = {
  creativeAssets: '/creative-assets'
}

export interface GetCreativesParams {
  projectId: string
  clientId: number
  maxCreatives?: number
}

export interface DeleteCreativeReq {
  clientId: number
  projectId: string
  creativeId: string
}

export interface CreateBatchIdResp {
  batchId: string
}

export const createBatchId = async ({ clientId }: { clientId: number }): Promise<CreateBatchIdResp> => {
  const path = `${paths.creativeAssets}/upload/batch-id?client_id=${clientId}`
  return await client.post<CreateBatchIdResp>({ path })
}

export interface CreateSignedUrlResp {
  signedUrl: string
}

export interface CreateSignedUrlReq {
  clientId: number
  projectId: string
  data: { filePath: string }
}

export const createSignedUrl = async (reqData: CreateSignedUrlReq): Promise<CreateSignedUrlResp> => {
  const { clientId, projectId, data } = reqData
  const path = `${paths.creativeAssets}/upload/signed-url?client_id=${clientId}&project_id=${projectId}`
  return await client.post<CreateSignedUrlResp>({ path, data })
}

export interface UploadFileReq {
  file: File
  signedURL: string
  onProgress?: (progress: number) => void
  onAbortRegistration?: (abort: () => void) => void
}

export const uploadFile = async (reqData: UploadFileReq): Promise<string> => {
  const { file, signedURL, onProgress, onAbortRegistration } = reqData

  // Use XMLHttpRequest for progress tracking
  return await new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Register abort function if callback provided
    if (onAbortRegistration != null) {
      onAbortRegistration(() => {
        xhr.abort()
      })
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress != null) {
        const percentComplete = Math.round((event.loaded / event.total) * 100)
        onProgress(percentComplete)
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.responseText)
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    }

    xhr.onerror = () => {
      reject(new Error('Network error during upload'))
    }

    xhr.onabort = () => {
      reject(new Error('Upload cancelled'))
    }

    xhr.open('PUT', signedURL)
    const filePath = (file as File & { path?: string }).path ?? file.name
    const lookupResult = mime.lookup(filePath)
    const mimeType = lookupResult === false ? 'application/octet-stream' : lookupResult
    xhr.setRequestHeader('Content-Type', mimeType)
    xhr.send(file)
  })
}

export interface Creative {
  id: string
  name: string
  ref: string
  fallback?: string
  size: number
  type: string
  updated?: number
  uploadedBy?: {
    id: number | string
    name?: string
  }
  files: string[]
  path: string
  htmlEntryType?: 'responsive' | 'combined_placement' | null
}

const appendLegacyFolderIdQueryParam = (path: string, legacyFolderId?: string): string => {
  if (legacyFolderId == null || legacyFolderId === '') {
    return path
  }
  return `${path}&legacy_folder_id=${encodeURIComponent(legacyFolderId)}`
}

export interface CreateCreativeReq {
  clientId: number
  projectId: string
  data: Creative
}

export const createCreative = async (params: CreateCreativeReq): Promise<Creative> => {
  const { clientId, projectId, data } = params
  const path = `${paths.creativeAssets}/upload/creative?client_id=${clientId}&project_id=${projectId}`
  return await client.post<Creative>({ path, data })
}

export const getCreatives = async (params: GetCreativesParams): Promise<Creative[]> => {
  const { projectId, clientId } = params
  const path = `${paths.creativeAssets}/creatives?client_id=${clientId}&project_id=${projectId}`
  return await client.get<Creative[]>({ path })
}

export interface GetCreativeParams {
  clientId: number
  projectId: string
  creativeId: string
  legacyFolderId?: string
}

export const getCreative = async (params: GetCreativeParams): Promise<Creative> => {
  const { clientId, projectId, creativeId, legacyFolderId } = params
  const path = appendLegacyFolderIdQueryParam(
    `${paths.creativeAssets}/creatives/${creativeId}?client_id=${clientId}&project_id=${projectId}`,
    legacyFolderId
  )
  return await client.get<Creative>({ path })
}

export type CreativeMetadataStatus = 'APPROVED' | 'NEEDS AMENDS' | 'READY FOR REVIEW' | 'ARCHIVED' | 'INTERNAL'
export interface CreativeMetadata {
  firebaseId: string
  status: CreativeMetadataStatus
  updatedBy?: number
  labels?: string[]
  logos?: string[]
  texts?: string[]
  colors?: string[]
  userDefinedTags?: string[]
  conversationId?: string
  /** Writable successor when the original conversation was closed by Twilio's timer. */
  newConversationId?: string
  createdBy?: number
}

export interface CreateOrUpdateCreativeMetadataPayload {
  status?: CreativeMetadataStatus
  labels?: string[]
  logos?: string[]
  texts?: string[]
  colors?: string[]
  userDefinedTags?: string[]
  conversationId?: string
  newConversationId?: string
}

export interface CreateOrUpdateCreativeMetadataReq {
  clientId: number
  projectId: string
  creativeId: string
  data: CreateOrUpdateCreativeMetadataPayload
}

export const createOrUpdateCreativeMetadata = async (
  params: CreateOrUpdateCreativeMetadataReq
): Promise<CreativeMetadata> => {
  const { clientId, projectId, creativeId, data } = params
  const path = `${paths.creativeAssets}/creatives-meta/${creativeId}?client_id=${clientId}&project_id=${projectId}`
  return await client.put<CreativeMetadata>({ path, data })
}

export interface GetCreativeMetadataParams {
  clientId: number
  projectId: string
  creativeId: string
}

export const getCreativeMetadata = async (params: GetCreativeMetadataParams): Promise<CreativeMetadata> => {
  const { clientId, projectId, creativeId } = params
  const path = `${paths.creativeAssets}/creatives-meta/${creativeId}?client_id=${clientId}&project_id=${projectId}`
  return await client.get<CreativeMetadata>({ path })
}

export interface GetCreativesMetadataParams {
  clientId: number
  projectId: string
}

export const getCreativesMetadata = async (params: GetCreativesMetadataParams): Promise<CreativeMetadata[]> => {
  const { clientId, projectId } = params
  const path = `${paths.creativeAssets}/creatives-meta?client_id=${clientId}&project_id=${projectId}`
  return await client.get<CreativeMetadata[]>({ path })
}

export interface DeleteCreativeParams {
  clientId: number
  projectId: string
  creativeId: string
  legacyFolderId?: string
}
export const deleteCreative = async (params: DeleteCreativeParams): Promise<void> => {
  const { clientId, projectId, creativeId, legacyFolderId } = params
  const path = appendLegacyFolderIdQueryParam(
    `${paths.creativeAssets}/creatives/${creativeId}?client_id=${clientId}&project_id=${projectId}`,
    legacyFolderId
  )
  await client.del({ path })
}

export const deleteCreativeMetadata = async (params: DeleteCreativeParams): Promise<void> => {
  const { clientId, projectId, creativeId } = params
  const path = `${paths.creativeAssets}/creatives-meta/${creativeId}?client_id=${clientId}&project_id=${projectId}`
  await client.del({ path })
}

export interface ProjectMetadata {
  projectId: string
  creativeNamingConvention: string
  createdBy?: number
  updatedBy?: number
  createdAt?: string
  updatedAt?: string
}

export interface CreateProjectMetadataReq {
  data: {
    projectId: string
    creativeNamingConvention: string
  }
  clientId: number
}

export const createProjectMetadata = async (params: CreateProjectMetadataReq): Promise<ProjectMetadata> => {
  const { data, clientId } = params
  const path = `${paths.creativeAssets}/project-metadata?client_id=${clientId}`
  return await client.post<ProjectMetadata>({ path, data })
}

export interface GetProjectMetadataParams {
  projectId: string
  clientId: number
}

export const getProjectMetadata = async (
  params: GetProjectMetadataParams
): Promise<ProjectMetadata | Record<string, never>> => {
  const { projectId, clientId } = params
  const path = `${paths.creativeAssets}/project-metadata/${projectId}?client_id=${clientId}`
  return await client.get<ProjectMetadata | Record<string, never>>({ path })
}

export interface UpdateProjectMetadataReq {
  projectId: string
  clientId: number
  data: {
    creativeNamingConvention: string
  }
}

export const updateProjectMetadata = async (params: UpdateProjectMetadataReq): Promise<ProjectMetadata> => {
  const { projectId, data, clientId } = params
  const path = `${paths.creativeAssets}/project-metadata/${projectId}?client_id=${clientId}`
  return await client.put<ProjectMetadata>({ path, data })
}

export interface DeleteProjectMetadataParams {
  projectId: string
  clientId: number
}

export const deleteProjectMetadata = async (params: DeleteProjectMetadataParams): Promise<void> => {
  const { projectId, clientId } = params
  const path = `${paths.creativeAssets}/project-metadata/${projectId}?client_id=${clientId}`
  await client.del({ path })
}

export interface CreateDownloadSignedUrlResp {
  signedUrl: string
}

export interface CreateDownloadSignedUrlReq {
  clientId: number
  projectId: string
  data: { filePath: string }
}

export const createDownloadSignedUrl = async (
  reqData: CreateDownloadSignedUrlReq
): Promise<CreateDownloadSignedUrlResp> => {
  const { projectId, clientId, data } = reqData
  const path = `${paths.creativeAssets}/download/signed-url?client_id=${clientId}&project_id=${projectId}`
  return await client.post<CreateDownloadSignedUrlResp>({ path, data })
}

export interface DownloadFileReq {
  signedURL: string
}

export const downloadFile = async ({ signedURL }: DownloadFileReq): Promise<Blob> => {
  const response = await fetch(signedURL, { method: 'GET' })
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`)
  }
  return await response.blob()
}
export interface VersionsParams {
  clientId: number
  projectId: string
  creativeId: string
  legacyFolderId?: string
}

export type Versions = Record<string, Creative>

export const getVersions = async (params: VersionsParams): Promise<Versions> => {
  const { clientId, projectId, creativeId, legacyFolderId } = params
  const path = appendLegacyFolderIdQueryParam(
    `${paths.creativeAssets}/versions/${creativeId}?client_id=${clientId}&project_id=${projectId}`,
    legacyFolderId
  )
  return await client.get<Versions>({ path })
}

export interface GetRequestedChangesParams {
  clientId: number
  projectId: string
  creativeId: string
}

export type Priority = 'low' | 'medium' | 'high'
export type Status = 'open' | 'resolved'
export interface RequestedChange {
  id: string
  creativeAssetId: string
  creativeVersionId: string
  projectId: string
  clientId: number
  conversationId: string
  title: string
  description: string
  priority: Priority
  status: Status
  createdBy: number
  createdAt: string
  updatedBy: number
  updatedAt: string
}

export const getRequestedChanges = async (params: GetRequestedChangesParams): Promise<RequestedChange[]> => {
  const { clientId, projectId, creativeId } = params
  const path = `${paths.creativeAssets}/requested-changes?client_id=${clientId}&project_id=${projectId}&creative_id=${creativeId}`
  return await client.get<RequestedChange[]>({ path })
}

export interface CreateRequestedChangeReq {
  clientId: number
  projectId: string
  creativeId: string
  data: {
    clientId: number
    projectId: string
    creativeId: string
    creativeVersionId: string
    conversationId: string
    title: string
    description: string
    priority: Priority
  }
}

export const createRequestedChange = async (params: CreateRequestedChangeReq): Promise<RequestedChange> => {
  const { clientId, projectId, data } = params
  const path = `${paths.creativeAssets}/requested-changes?client_id=${clientId}&project_id=${projectId}`
  return await client.post<RequestedChange>({ path, data })
}

export interface GetRequestedChangeParams {
  clientId: number
  changeId: string
}

export const getRequestedChange = async (params: GetRequestedChangeParams): Promise<RequestedChange> => {
  const { clientId, changeId } = params
  const path = `${paths.creativeAssets}/requested-changes/${changeId}?client_id=${clientId}`
  return await client.get<RequestedChange>({ path })
}

export interface UpdateRequestedChangeReq {
  clientId: number
  changeId: string
  data: {
    status?: Status
    title?: string
    description?: string
    priority?: Priority
  }
}

export const updateRequestedChange = async (params: UpdateRequestedChangeReq): Promise<RequestedChange> => {
  const { clientId, changeId, data } = params
  const path = `${paths.creativeAssets}/requested-changes/${changeId}?client_id=${clientId}`
  return await client.patch<RequestedChange>({ path, data })
}

export interface DeleteRequestedChangeReq {
  clientId: number
  changeId: string
}

export const deleteRequestedChange = async (params: DeleteRequestedChangeReq): Promise<void> => {
  const { clientId, changeId } = params
  const path = `${paths.creativeAssets}/requested-changes/${changeId}?client_id=${clientId}`
  await client.del({ path })
}
